import { Container, cn } from "@/components/common"
import { hasPrebakedVariants } from "@lib/util/image-variants"
import Image from "next/image"
import React from "react"

import ProductPlaceholder from "@modules/common/components/product-placeholder"

type ThumbnailProps = {
  thumbnail?: string | null
  // TODO: Fix image typings
  images?: any[] | null
  size?: "small" | "medium" | "large" | "full" | "square"
  isFeatured?: boolean
  className?: string
  "data-testid"?: string
  alt?: string
}

const Thumbnail: React.FC<ThumbnailProps> = ({
  thumbnail,
  images,
  size = "small",
  isFeatured,
  className,
  "data-testid": dataTestid,
  alt = "Product image",
}) => {
  const initialImage = thumbnail || images?.[0]?.url

  return (
    <Container
      className={cn(
        "relative w-full overflow-hidden p-4 bg-ui-bg-subtle shadow-elevation-card-rest rounded-large group-hover:shadow-elevation-card-hover transition-shadow ease-in-out duration-150",
        className,
        {
          "aspect-[3/4]": isFeatured,
          "aspect-square": !isFeatured || size === "square",
          "w-[180px]": size === "small",
          "w-[290px]": size === "medium",
          "w-[440px]": size === "large",
          "w-full": size === "full",
        }
      )}
      data-testid={dataTestid}
    >
      <ImageOrPlaceholder image={initialImage} size={size} alt={alt} />
    </Container>
  )
}

const ImageOrPlaceholder = ({
  image,
  size,
  alt = "Product image",
}: Pick<ThumbnailProps, "size" | "alt"> & { image?: string }) => {
  const placeholderSize = size === "small" ? "sm" : size === "medium" ? "md" : "lg"

  return image ? (
    <Image
      src={image}
      alt={alt}
      className="absolute inset-0 object-contain object-center"
      draggable={false}
      quality={50}
      // Без нарезанных вариантов ширины не существует: см. lib/util/image-variants.
      unoptimized={!hasPrebakedVariants(image)}
      sizes="(max-width: 576px) 280px, (max-width: 768px) 360px, (max-width: 992px) 480px, 800px"
      fill
    />
  ) : (
    <div className="w-full h-full absolute inset-0">
      <ProductPlaceholder size={placeholderSize} />
    </div>
  )
}

export default Thumbnail
